import { useEffect, useMemo, useState } from 'react';
import {
  AddRounded as AddIcon,
  DeleteOutlineRounded as DeleteIcon,
  EditOutlined as EditIcon,
  PlayArrowRounded as ActivateIcon,
  TimelineRounded as TimelineIcon
} from '@mui/icons-material';
import { Field, Select, TextInput } from './WizardControls';
import SolarHijriDatePicker from './SolarHijriDatePicker';

const DAY_MS = 24 * 60 * 60 * 1000;
const EMPTY_CATALOG = [];
const sectionTypeLabels = {
  PHASE: 'فاز',
  BUILDING: 'ساختمان',
  BLOCK: 'بلوک'
};

function parseDate(value) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  }).format(date);
}

function durationInDays(startValue, finishValue) {
  const start = parseDate(startValue);
  const finish = parseDate(finishValue);
  if (!start || !finish || finish < start) return null;
  return Math.floor((finish - start) / DAY_MS) + 1;
}

export function buildActivityPlan(catalog, form) {
  if (!catalog.length) return [];
  const startIndex = catalog.findIndex((item) => item.name === form.starting_activity);
  const visibleCatalog = catalog.slice(startIndex >= 0 ? startIndex : 0);
  const existingByName = new Map((form.activities || []).map((item) => [item.name, item]));

  return visibleCatalog.map((item) => {
    const existing = existingByName.get(item.name) || {};
    return {
      name: item.name,
      planned_start: existing.planned_start || '',
      planned_finish: existing.planned_finish || '',
      section: form.section || null,
      description: existing.description || ''
    };
  });
}

function ScheduleGantt({ activities, catalog, scopeLabel }) {
  const catalogByName = useMemo(
    () => new Map(catalog.map((item) => [item.name, item])),
    [catalog]
  );
  const datedActivities = activities.filter((activity) => {
    const start = parseDate(activity.planned_start);
    const finish = parseDate(activity.planned_finish);
    return start && finish && finish >= start;
  });

  if (!datedActivities.length) {
    return (
      <div className="dev-gantt-empty">
        <TimelineIcon />
        <strong>نمودار پس از ثبت تاریخ ها نمایش داده می شود</strong>
        <span>{scopeLabel}</span>
      </div>
    );
  }

  const starts = datedActivities.map((activity) => parseDate(activity.planned_start).getTime());
  const finishes = datedActivities.map((activity) => parseDate(activity.planned_finish).getTime());
  const rangeStart = new Date(Math.min(...starts));
  const rangeFinish = new Date(Math.max(...finishes));
  const totalDays = Math.max(Math.floor((rangeFinish - rangeStart) / DAY_MS) + 1, 1);
  const tickCount = Math.min(6, Math.max(2, Math.ceil(totalDays / 45) + 1));
  const ticks = Array.from({ length: tickCount }, (_, index) => {
    const ratio = tickCount === 1 ? 0 : index / (tickCount - 1);
    const date = new Date(rangeStart.getTime() + Math.round((totalDays - 1) * ratio) * DAY_MS);
    return { ratio, label: formatDate(date) };
  });

  return (
    <div className="dev-gantt" dir="rtl">
      <div className="dev-gantt-summary">
        <span>{scopeLabel}</span>
        <strong>{formatDate(rangeStart)} تا {formatDate(rangeFinish)}</strong>
        <small>{totalDays.toLocaleString('fa-IR')} روز</small>
      </div>
      <div className="dev-gantt-scroll">
        <div className="dev-gantt-grid" style={{ '--gantt-rows': activities.length }}>
          <div className="dev-gantt-label-head">فعالیت</div>
          <div className="dev-gantt-axis" dir="rtl">
            {ticks.map((tick) => (
              <span key={`${tick.ratio}-${tick.label}`} style={{ right: `${tick.ratio * 100}%` }}>
                {tick.label}
              </span>
            ))}
          </div>
          {activities.map((activity, index) => {
            const definition = catalogByName.get(activity.name) || {};
            const start = parseDate(activity.planned_start);
            const finish = parseDate(activity.planned_finish);
            const isValid = start && finish && finish >= start;
            const right = isValid ? ((start - rangeStart) / DAY_MS / totalDays) * 100 : 0;
            const width = isValid ? Math.max((((finish - start) / DAY_MS + 1) / totalDays) * 100, 1.25) : 0;
            const isMilestone = definition.type === 'MILESTONE';
            return (
              <div className="dev-gantt-row" key={activity.name} style={{ gridRow: index + 2 }}>
                <div className="dev-gantt-label">
                  <span>{definition.display_order}</span>
                  <strong>{definition.label || activity.name_label || activity.name}</strong>
                </div>
                <div className="dev-gantt-track" dir="rtl">
                  {ticks.map((tick) => <i key={tick.ratio} style={{ right: `${tick.ratio * 100}%` }} />)}
                  {isValid && (
                    <div
                      className={`dev-gantt-bar${isMilestone ? ' milestone' : ''}`}
                      style={{ right: `${right}%`, width: isMilestone ? undefined : `${width}%` }}
                      title={`${definition.label || activity.name}: ${activity.planned_start} تا ${activity.planned_finish}`}
                    >
                      {!isMilestone && <span>{durationInDays(activity.planned_start, activity.planned_finish)?.toLocaleString('fa-IR')} روز</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ProjectScheduleStep({
  activeScheduleVersion,
  busy,
  editingScheduleVersionId,
  projectBundle,
  scheduleForm,
  selectedProject,
  statusLabels,
  onActivateSchedule,
  onDeleteSchedule,
  onEditSchedule,
  onNewSchedule,
  onSaveSchedule,
  onScheduleFormChange
}) {
  const catalog = projectBundle.activityCatalog || EMPTY_CATALOG;
  const versions = useMemo(
    () => [...projectBundle.schedules].sort((left, right) => right.number - left.number),
    [projectBundle.schedules]
  );
  const [selectedVersionId, setSelectedVersionId] = useState('');

  useEffect(() => {
    if (selectedVersionId && versions.some((version) => String(version.id) === String(selectedVersionId))) return;
    setSelectedVersionId(activeScheduleVersion?.id || versions[0]?.id || '');
  }, [activeScheduleVersion?.id, selectedVersionId, versions]);

  const plannedActivities = useMemo(
    () => buildActivityPlan(catalog, scheduleForm),
    [catalog, scheduleForm]
  );
  const selectedVersion = versions.find((version) => String(version.id) === String(selectedVersionId));
  const hasEditorInput = Boolean(
    scheduleForm.reason
    || scheduleForm.effective_from
    || scheduleForm.section
    || scheduleForm.starting_activity !== 'SITE_PREPARATION'
    || plannedActivities.some((activity) => activity.planned_start || activity.planned_finish)
  );
  const chartActivities = editingScheduleVersionId || hasEditorInput
    ? plannedActivities
    : (selectedVersion?.activities || plannedActivities);
  const selectedSection = projectBundle.sections.find(
    (section) => String(section.id) === String(scheduleForm.section)
  );
  const chartSection = editingScheduleVersionId || hasEditorInput
    ? selectedSection
    : projectBundle.sections.find(
      (section) => String(section.id) === String(selectedVersion?.activities?.[0]?.section)
    );
  const scopeLabel = chartSection
    ? `${sectionTypeLabels[chartSection.type] || chartSection.type} ${chartSection.name}`
    : 'کل پروژه';

  const updateSetup = (changes) => {
    const nextForm = { ...scheduleForm, ...changes };
    onScheduleFormChange({
      ...nextForm,
      activities: buildActivityPlan(catalog, nextForm)
    });
  };

  const updateActivityDate = (index, field, value) => {
    const activities = buildActivityPlan(catalog, scheduleForm).map((activity) => ({ ...activity }));
    const previousValue = activities[index][field];
    activities[index][field] = value;
    if (
      field === 'planned_finish'
      && value
      && activities[index + 1]
      && (!activities[index + 1].planned_start || activities[index + 1].planned_start === previousValue)
    ) {
      activities[index + 1].planned_start = value;
    }
    onScheduleFormChange({ ...scheduleForm, activities });
  };

  const planIssue = useMemo(() => {
    if (!plannedActivities.length) return 'فهرست مراحل در دسترس نیست.';
    let previousFinish = null;
    for (const activity of plannedActivities) {
      if (!activity.planned_start || !activity.planned_finish) return 'تاریخ شروع و پایان همه مراحل را ثبت کنید.';
      if (activity.planned_finish < activity.planned_start) return 'پایان هر مرحله باید بعد از شروع آن باشد.';
      if (previousFinish && activity.planned_start < previousFinish) return 'مراحل برنامه نباید با یکدیگر هم پوشانی داشته باشند.';
      previousFinish = activity.planned_finish;
    }
    return '';
  }, [plannedActivities]);

  const progress = Math.min(100, Math.max(0, Number(selectedProject?.current_progress || 0)));

  return (
    <div className="dev-step-layout dev-schedule-step dev-schedule-workspace">
      <form className="dev-panel dev-step-main dev-schedule-editor" onSubmit={onSaveSchedule}>
        <div className="dev-panel-head dev-schedule-heading">
          <div>
            <span className="dev-section-kicker">برنامه مبنا</span>
            <h2>{editingScheduleVersionId ? 'ویرایش نسخه پیش نویس' : 'تنظیم برنامه زمان بندی ساخت'}</h2>
          </div>
          {editingScheduleVersionId && (
            <button type="button" className="dev-btn secondary" onClick={onNewSchedule} disabled={busy}>
              <AddIcon fontSize="small" /> نسخه جدید
            </button>
          )}
        </div>

        <div className="dev-schedule-setup">
          <Field label="مرحله فعلی پروژه">
            <Select value={scheduleForm.starting_activity} onChange={(value) => updateSetup({ starting_activity: value })}>
              {catalog.map((item) => (
                <option key={item.name} value={item.name}>{item.display_order}. {item.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="محدوده اجرا">
            <Select value={scheduleForm.section} onChange={(value) => updateSetup({ section: value })}>
              <option value="">کل پروژه</option>
              {projectBundle.sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {sectionTypeLabels[section.type] || section.type} - {section.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="تاریخ اثرگذاری نسخه (شمسی)">
            <SolarHijriDatePicker
              value={scheduleForm.effective_from}
              onChange={(value) => updateSetup({ effective_from: value })}
              ariaLabel="تاریخ اثرگذاری نسخه"
            />
          </Field>
          <Field label="دلیل ایجاد نسخه">
            <TextInput value={scheduleForm.reason} onChange={(value) => updateSetup({ reason: value })} placeholder="مثلا برنامه اولیه یا بازنگری زمان بندی" />
          </Field>
        </div>

        <div className="dev-schedule-table-wrap">
          <div className="dev-schedule-table">
            <div className="dev-schedule-table-head">
              <span>مرحله</span>
              <span>وزن</span>
              <span>شروع</span>
              <span>پایان</span>
              <span>مدت</span>
            </div>
            {plannedActivities.map((activity, index) => {
              const definition = catalog.find((item) => item.name === activity.name) || {};
              const duration = durationInDays(activity.planned_start, activity.planned_finish);
              const invalid = activity.planned_start && activity.planned_finish && activity.planned_finish < activity.planned_start;
              return (
                <div className={`dev-schedule-table-row${invalid ? ' invalid' : ''}`} key={activity.name}>
                  <div className="dev-schedule-activity-name">
                    <span>{definition.display_order}</span>
                    <div>
                      <strong>{definition.label}</strong>
                      {index === 0 && <small>مرحله فعلی</small>}
                    </div>
                  </div>
                  <span className="dev-schedule-weight">{(Number(definition.weight || 0) * 100).toLocaleString('fa-IR')}٪</span>
                  <SolarHijriDatePicker
                    value={activity.planned_start}
                    min={plannedActivities[index - 1]?.planned_finish || ''}
                    onChange={(value) => updateActivityDate(index, 'planned_start', value)}
                    ariaLabel={`تاریخ شروع ${definition.label}`}
                  />
                  <SolarHijriDatePicker
                    value={activity.planned_finish}
                    min={activity.planned_start || ''}
                    onChange={(value) => updateActivityDate(index, 'planned_finish', value)}
                    ariaLabel={`تاریخ پایان ${definition.label}`}
                  />
                  <span className="dev-schedule-duration">{duration ? `${duration.toLocaleString('fa-IR')} روز` : '-'}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dev-schedule-submit">
          <span className={planIssue ? 'dev-form-note warning' : 'dev-form-note success'}>
            {planIssue || `${plannedActivities.length.toLocaleString('fa-IR')} مرحله آماده ذخیره است.`}
          </span>
          <button className="dev-btn primary" disabled={Boolean(planIssue) || !selectedProject || busy}>
            {editingScheduleVersionId ? 'ذخیره تغییرات نسخه' : 'ایجاد نسخه برنامه'}
          </button>
        </div>
      </form>

      <aside className="dev-panel dev-step-side dev-schedule-versions">
        <div className="dev-panel-head">
          <div>
            <span className="dev-section-kicker">تاریخچه</span>
            <h2>نسخه های برنامه</h2>
          </div>
          <span className="dev-count-pill">{versions.length.toLocaleString('fa-IR')}</span>
        </div>

        <div className="dev-schedule-progress-summary">
          <div>
            <strong>{progress.toLocaleString('fa-IR')}٪</strong>
            <span>پیشرفت فعلی پروژه</span>
          </div>
          <div className="dev-progress"><span style={{ width: `${progress}%` }} /></div>
        </div>

        <div className="dev-version-list">
          {versions.length === 0 && <p className="dev-empty-copy">هنوز نسخه ای ثبت نشده است.</p>}
          {versions.map((version) => {
            const isSelected = String(version.id) === String(selectedVersionId);
            const isDraft = version.status === 'draft';
            const target = projectBundle.sections.find(
              (section) => String(section.id) === String(version.activities?.[0]?.section)
            );
            return (
              <div
                className={`dev-version-row${isSelected ? ' selected' : ''}`}
                key={version.id}
              >
                <button
                  type="button"
                  className="dev-version-select"
                  onClick={() => setSelectedVersionId(version.id)}
                >
                  <div className="dev-version-main">
                    <div>
                      <strong>نسخه {Number(version.number).toLocaleString('fa-IR')}</strong>
                      <span className={`dev-status-dot ${version.status}`} />
                    </div>
                    <small>{statusLabels[version.status] || version.status}</small>
                    <span>{target ? `${sectionTypeLabels[target.type] || target.type} ${target.name}` : 'کل پروژه'}</span>
                  </div>
                </button>
                <div className="dev-version-actions">
                  {isDraft && (
                    <button
                      type="button"
                      className="dev-icon-btn"
                      title="ویرایش نسخه"
                      aria-label="ویرایش نسخه"
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedVersionId(version.id);
                        onEditSchedule(version);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                  )}
                  {isDraft && (
                    <button
                      type="button"
                      className="dev-icon-btn success"
                      title="فعال سازی نسخه"
                      aria-label="فعال سازی نسخه"
                      disabled={busy || !version.activities?.length}
                      onClick={(event) => {
                        event.stopPropagation();
                        onActivateSchedule(version.id);
                      }}
                    >
                      <ActivateIcon fontSize="small" />
                    </button>
                  )}
                  {isDraft && (
                    <button
                      type="button"
                      className="dev-icon-btn danger"
                      title="حذف نسخه"
                      aria-label="حذف نسخه"
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (window.confirm(`نسخه ${version.number} حذف شود؟`)) onDeleteSchedule(version.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <section className="dev-panel dev-step-full dev-schedule-gantt-panel">
        <div className="dev-panel-head">
          <div>
            <span className="dev-section-kicker">نمای زمان</span>
            <h2>نمودار گانت</h2>
          </div>
          {selectedVersion && !editingScheduleVersionId && !hasEditorInput && (
            <span className="dev-gantt-version">نسخه {Number(selectedVersion.number).toLocaleString('fa-IR')}</span>
          )}
        </div>
        <ScheduleGantt activities={chartActivities} catalog={catalog} scopeLabel={scopeLabel} />
      </section>
    </div>
  );
}
